import cn from 'classnames';
import { throttle } from 'lodash';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { inIframe } from '../../utils/location';
import styles from './StickyFooterButton.module.css';

const SPACING_TO_CONTAINER = 20;

export const StickyFooterButton = ({ children }: { children: ReactNode }) => {
  const { ref, isIntersecting } = useIntersection<HTMLDivElement>({ offset: SPACING_TO_CONTAINER });
  const floatingButton = isIntersecting || inIframe();

  return (
    <div className={cn(styles.stickyContainer, { [c(styles.sticky)]: floatingButton })}>
      <div ref={ref} className={cn({ [c(styles.withBackground)]: floatingButton })}>
        {children}
      </div>
    </div>
  );
};

const useIntersection = <T extends HTMLElement>({ offset = 0 }: { offset?: number } = {}) => {
  const ref = useRef<T | null>(null);

  const [isIntersecting, setIsIntersecting] = useState(false);
  const isPhone = useBreakpoint('phone');

  useEffect(() => {
    const isPollsSite = document.body.id === 'PollsSite';

    if (!isPollsSite || !isPhone) {
      setIsIntersecting(false);
      return;
    }

    const observe = () => {
      const container = document.getElementById('content');
      const element = ref.current;

      if (!container || !element) return;

      const containerRect = container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();

      const isIntersecting = !(elementRect.bottom + offset >= containerRect.bottom);

      setIsIntersecting(isIntersecting);
    };

    observe();

    const throttledObserve = throttle(observe, 50);

    document.addEventListener('scroll', throttledObserve);
    window.addEventListener('resize', throttledObserve);

    return () => {
      document.removeEventListener('scroll', throttledObserve);
      window.removeEventListener('resize', throttledObserve);
    };
  }, [isPhone, offset]);

  return { ref, isIntersecting };
};
